/**
 * PHSIMS Web Admin - Custom Login Page
 * Author: Iswe Solutions Team
 * 
 * Login page matching the PHSIMS technical documentation website design.
 * Dark slate background with white card, gradient title, Zambian theme colors.
 * Supports both local authentication and Keycloak SSO.
 */

import React, { useState, useEffect } from 'react';
import { useLogin, useNotify, Notification } from 'react-admin';
import {
    Box,
    TextField,
    Button,
    Typography,
    CircularProgress,
    Alert,
    InputAdornment,
    IconButton,
    Divider,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import KeyIcon from '@mui/icons-material/Key';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { fetchSSOConfig, keycloakLogin } from '../services/keycloakService';
import { AppFooter } from './common/AppFooter';
import { IS_DEV_INSTANCE } from '../utils/publicUrls';

const LoginPage: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [ssoLoading, setSsoLoading] = useState(false);
    const [error, setError] = useState('');
    const [ssoEnabled, setSsoEnabled] = useState(false);
    const [ssoAvailable, setSsoAvailable] = useState(true);

    // Second-factor step: shown after the password is accepted for an enrolled
    // user. method drives the wording (authenticator app vs emailed code).
    const [mfaStage, setMfaStage] = useState(false);
    const [mfaMethod, setMfaMethod] = useState<'totp' | 'email'>('totp');
    const [mfaCode, setMfaCode] = useState('');
    const [resendInfo, setResendInfo] = useState('');

    const login = useLogin();
    const notify = useNotify();
    
    // Check if SSO is enabled and available (requires HTTPS or localhost)
    useEffect(() => {
        const checkSSOConfig = async () => {
            try {
                // Check if Web Crypto API is available (required for PKCE)
                const isSecureContext = window.isSecureContext || 
                    window.location.hostname === 'localhost' || 
                    window.location.hostname === '127.0.0.1';
                
                if (!isSecureContext || !window.crypto?.subtle) {
                    console.log('SSO requires HTTPS - disabling SSO button');
                    setSsoAvailable(false);
                    return;
                }
                
                const config = await fetchSSOConfig();
                setSsoEnabled(config.enabled);
            } catch (err) {
                console.log('SSO not available, using local auth');
            }
        };
        checkSSOConfig();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await login({ username, password });
        } catch (err: unknown) {
            setLoading(false);
            // Not a failure: the password was accepted and a second factor is
            // needed. Switch to the code step instead of showing an error.
            if (err && (err as { mfaRequired?: boolean }).mfaRequired) {
                const method = (err as { method?: 'totp' | 'email' }).method || 'totp';
                setMfaMethod(method);
                setMfaStage(true);
                setError('');
                setMfaCode('');
                setResendInfo(method === 'email' ? 'We sent a 6-digit code to your email address.' : '');
                return;
            }
            const errorMessage = err instanceof Error ? err.message : 'Invalid username or password';
            setError(errorMessage);
            notify(errorMessage, { type: 'error' });
        }
    };

    const handleMfaSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            // Success path redirects from inside the auth provider.
            await login({ username, password, mfaCode });
        } catch (err: unknown) {
            setLoading(false);
            const msg = err instanceof Error ? err.message : 'Invalid authentication code';
            setError(msg);
        }
    };

    const handleResendCode = async () => {
        setError('');
        setResendInfo('');
        try {
            // Re-posting the password with no code triggers a fresh send. The
            // backend rate-limits this, so mashing the button will not flood.
            await login({ username, password });
        } catch (err: unknown) {
            if (err && (err as { mfaRequired?: boolean }).mfaRequired) {
                setResendInfo('A new code is on its way — check your email.');
                return;
            }
            const msg = err instanceof Error ? err.message : 'Could not resend the code. Please try again.';
            setError(msg);
        }
    };

    const handleBackToPassword = () => {
        setMfaStage(false);
        setMfaCode('');
        setError('');
        setResendInfo('');
        setLoading(false);
    };

    const handleSSOLogin = async () => {
        // Check if secure context before attempting SSO
        if (!window.isSecureContext && window.location.hostname !== 'localhost') {
            setError('SSO requires HTTPS. Please use local login or access via HTTPS.');
            notify('SSO requires HTTPS connection', { type: 'warning' });
            return;
        }
        
        setSsoLoading(true);
        setError('');
        try {
            await keycloakLogin(window.location.origin + '/');
        } catch (err: unknown) {
            setSsoLoading(false);
            const errorMessage = err instanceof Error ? err.message : 'SSO login failed';
            // Provide more helpful error for crypto issues
            if (errorMessage.includes('Crypto') || errorMessage.includes('crypto')) {
                setError('SSO requires HTTPS. Please use local login or configure HTTPS.');
            } else {
                setError(errorMessage);
            }
            notify(errorMessage, { type: 'error' });
        }
    };

    return (
        <Box
            sx={{
                height: '100vh',
                width: '100vw',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                // Uniform deep forest green background (Zambian theme)
                background: '#0d3322',
                position: 'fixed',
                top: 0,
                left: 0,
            }}
        >
            {/* White Login Card */}
            <Box
                sx={{
                    background: 'white',
                    padding: { xs: '24px 20px', sm: '32px 40px' },
                    borderRadius: { xs: '16px', sm: '20px' },
                    boxShadow: '0 25px 80px rgba(0,0,0,0.5)',
                    width: { xs: '100%', sm: 460 },
                    maxWidth: { xs: '94%', sm: '94%' },
                    position: 'relative',
                    zIndex: 1,
                    // Orange top ribbon
                    borderTop: '5px solid #ef7d00',
                }}
            >
                {/* Header Section */}
                <Box sx={{ textAlign: 'center', mb: { xs: 2.5, sm: 3 } }}>
                    {/* Logo */}
                    <Box
                        component="img"
                        src="/znphi_logo.jpg"
                        alt="ZNPHI Logo"
                        sx={{
                            width: { xs: 70, sm: 90 },
                            height: 'auto',
                            borderRadius: '8px',
                            mb: 1.5,
                            objectFit: 'contain',
                        }}
                    />
                    
                    {/* System Name - Stacked, Title Case, Strong Green */}
                    <Typography
                        sx={{
                            color: '#0d5c2e',
                            fontSize: { xs: '16px', sm: '20px' },
                            fontWeight: 800,
                            lineHeight: 1.3,
                            mb: 0.5,
                            letterSpacing: '0.2px',
                        }}
                    >
                        Public Health Security and<br />Information Management System
                    </Typography>
                    
                    {/* PHSIMS Title - Orange */}
                    <Typography
                        variant="h4"
                        sx={{
                            fontSize: { xs: '24px', sm: '28px' },
                            fontWeight: 800,
                            letterSpacing: '2px',
                            mt: 0.5,
                            color: '#ef7d00',
                        }}
                    >
                        PHSIMS
                    </Typography>
                </Box>

                {IS_DEV_INSTANCE && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2, borderRadius: '10px' }}
                    >
                        This is a DEV instance. Please request login access to production from the system administrator.
                    </Alert>
                )}

                {/* Error / Maintenance Message */}
                {error && (
                    <Alert 
                        severity={error.includes('maintenance') ? 'warning' : 'error'}
                        sx={{ 
                            mb: 2,
                            borderRadius: '10px',
                            border: error.includes('maintenance')
                                ? '1px solid rgba(245, 158, 11, 0.3)'
                                : '1px solid rgba(239, 68, 68, 0.3)',
                            fontSize: '13px',
                            py: 0.5,
                        }}
                    >
                        {error}
                    </Alert>
                )}

                {/* Login Form (password step) */}
                {!mfaStage && (
                <form onSubmit={handleSubmit}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {/* Username Field */}
                        <TextField
                            placeholder="Username"
                            variant="outlined"
                            fullWidth
                            required
                            autoFocus
                            autoComplete="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            disabled={loading}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonOutlineIcon sx={{ color: '#198a4b', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    backgroundColor: '#f8fafc',
                                    fontWeight: 500,
                                    minHeight: '48px',
                                    '& fieldset': {
                                        borderWidth: '2px',
                                        borderColor: '#e2e8f0',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#198a4b',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#198a4b',
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: 'white',
                                        boxShadow: '0 0 0 3px rgba(25, 138, 75, 0.1)',
                                    },
                                },
                                '& .MuiOutlinedInput-input': {
                                    padding: '12px 12px 12px 0',
                                    fontSize: '14px',
                                },
                            }}
                        />

                        {/* Password Field with Toggle */}
                        <TextField
                            placeholder="Password"
                            type={showPassword ? 'text' : 'password'}
                            variant="outlined"
                            fullWidth
                            required
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlinedIcon sx={{ color: '#198a4b', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="small"
                                            sx={{ 
                                                color: '#94a3b8',
                                                '&:hover': {
                                                    color: '#ef7d00',
                                                    bgcolor: 'rgba(239, 125, 0, 0.08)',
                                                }
                                            }}
                                        >
                                            {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    backgroundColor: '#f8fafc',
                                    fontWeight: 500,
                                    minHeight: '48px',
                                    '& fieldset': {
                                        borderWidth: '2px',
                                        borderColor: '#e2e8f0',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#198a4b',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#198a4b',
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: 'white',
                                        boxShadow: '0 0 0 3px rgba(25, 138, 75, 0.1)',
                                    },
                                },
                                '& .MuiOutlinedInput-input': {
                                    padding: '12px 0',
                                    fontSize: '14px',
                                },
                            }}
                        />

                        {/* Login Button - Green with orange hover */}
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            startIcon={!loading && <LoginIcon sx={{ fontSize: 18 }} />}
                            sx={{
                                mt: 0.5,
                                py: 1.2,
                                borderRadius: '10px',
                                fontSize: '14px',
                                fontWeight: 700,
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #198a4b 0%, #14703d 100%)',
                                boxShadow: '0 4px 20px rgba(25, 138, 75, 0.35)',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #ef7d00 0%, #f59e0b 100%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 30px rgba(239, 125, 0, 0.4)',
                                },
                                '&:active': {
                                    transform: 'translateY(0)',
                                },
                                '&:disabled': {
                                    background: '#cbd5e1',
                                    boxShadow: 'none',
                                    transform: 'none',
                                }
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={20} sx={{ color: 'white' }} />
                            ) : (
                                'Login'
                            )}
                        </Button>
                        
                        {/* Forgot Password Link */}
                        <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                            <Typography
                                component="a"
                                href="/forgot-password"
                                sx={{
                                    color: '#64748b',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        color: '#ef7d00',
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                Forgot password?
                            </Typography>
                        </Box>
                        
                        {/* SSO Login Button - Only show if Keycloak is enabled AND secure context available */}
                        {ssoEnabled && ssoAvailable && (
                            <>
                                <Divider 
                                    sx={{ 
                                        my: 1.5,
                                        color: '#94a3b8',
                                        fontSize: '12px',
                                        '&::before, &::after': {
                                            borderColor: '#e2e8f0',
                                        }
                                    }}
                                >
                                    or
                                </Divider>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    fullWidth
                                    disabled={ssoLoading}
                                    onClick={handleSSOLogin}
                                    startIcon={!ssoLoading && <KeyIcon sx={{ fontSize: 18 }} />}
                                    sx={{
                                        py: 1.2,
                                        borderRadius: '10px',
                                        fontSize: '14px',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        borderWidth: '2px',
                                        borderColor: '#ef7d00',
                                        color: '#ef7d00',
                                        transition: 'all 0.3s',
                                        '&:hover': {
                                            borderWidth: '2px',
                                            borderColor: '#ef7d00',
                                            background: 'rgba(239, 125, 0, 0.08)',
                                            transform: 'translateY(-2px)',
                                        },
                                        '&:active': {
                                            transform: 'translateY(0)',
                                        },
                                        '&:disabled': {
                                            borderColor: '#cbd5e1',
                                            color: '#cbd5e1',
                                        }
                                    }}
                                >
                                    {ssoLoading ? (
                                        <CircularProgress size={20} sx={{ color: '#ef7d00' }} />
                                    ) : (
                                        'Login with SSO'
                                    )}
                                </Button>
                            </>
                        )}
                    </Box>
                </form>
                )}

                {/* Second-factor step */}
                {mfaStage && (
                <form onSubmit={handleMfaSubmit}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                        <Box sx={{ textAlign: 'center', mb: 0.5 }}>
                            {mfaMethod === 'email'
                                ? <MarkEmailReadOutlinedIcon sx={{ fontSize: 44, color: '#198a4b' }} />
                                : <ShieldOutlinedIcon sx={{ fontSize: 44, color: '#198a4b' }} />}
                            <Typography sx={{ fontWeight: 800, color: '#0d5c2e', fontSize: '18px', mt: 0.5 }}>
                                Two-step verification
                            </Typography>
                            <Typography sx={{ color: '#64748b', fontSize: '13px', mt: 0.5, lineHeight: 1.5 }}>
                                {mfaMethod === 'email'
                                    ? 'Enter the 6-digit code we emailed you to finish signing in.'
                                    : 'Enter the 6-digit code from your authenticator app to finish signing in.'}
                            </Typography>
                        </Box>

                        {resendInfo && (
                            <Alert severity="info" sx={{ borderRadius: '10px', fontSize: '13px', py: 0.25 }}>
                                {resendInfo}
                            </Alert>
                        )}

                        <TextField
                            placeholder="000000"
                            variant="outlined"
                            fullWidth
                            required
                            autoFocus
                            value={mfaCode}
                            onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            disabled={loading}
                            inputProps={{
                                inputMode: 'numeric',
                                autoComplete: 'one-time-code',
                                maxLength: 6,
                                style: { textAlign: 'center', letterSpacing: '10px', fontSize: '24px', fontWeight: 700 },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <KeyIcon sx={{ color: '#198a4b', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    backgroundColor: '#f8fafc',
                                    minHeight: '52px',
                                    '& fieldset': { borderWidth: '2px', borderColor: '#e2e8f0' },
                                    '&:hover fieldset': { borderColor: '#198a4b' },
                                    '&.Mui-focused fieldset': { borderColor: '#198a4b' },
                                    '&.Mui-focused': { backgroundColor: 'white', boxShadow: '0 0 0 3px rgba(25, 138, 75, 0.1)' },
                                },
                            }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading || mfaCode.length < 6}
                            startIcon={!loading && <VerifiedUserIcon sx={{ fontSize: 18 }} />}
                            sx={{
                                py: 1.2,
                                borderRadius: '10px',
                                fontSize: '14px',
                                fontWeight: 700,
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #198a4b 0%, #14703d 100%)',
                                boxShadow: '0 4px 20px rgba(25, 138, 75, 0.35)',
                                transition: 'all 0.3s',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #ef7d00 0%, #f59e0b 100%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 30px rgba(239, 125, 0, 0.4)',
                                },
                                '&:disabled': { background: '#cbd5e1', boxShadow: 'none', transform: 'none' },
                            }}
                        >
                            {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Verify and sign in'}
                        </Button>

                        {mfaMethod === 'email' && (
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                    component="button"
                                    type="button"
                                    onClick={handleResendCode}
                                    sx={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        color: '#198a4b', fontSize: '13px', fontWeight: 600,
                                        '&:hover': { color: '#ef7d00', textDecoration: 'underline' },
                                    }}
                                >
                                    Didn't get it? Resend code
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ textAlign: 'center' }}>
                            <Button
                                type="button"
                                onClick={handleBackToPassword}
                                startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                                sx={{
                                    color: '#64748b', fontSize: '13px', fontWeight: 500, textTransform: 'none',
                                    '&:hover': { color: '#ef7d00', background: 'transparent' },
                                }}
                            >
                                Use a different account
                            </Button>
                        </Box>
                    </Box>
                </form>
                )}

                {/* Footer */}
                <AppFooter variant="auth" />
            </Box>

            <Notification />
        </Box>
    );
};

export default LoginPage;
